/*============================================================================
  Miller's Leather Shop - Modern Theme Enhancements
  Vanilla JS enhancements for improved UX and performance
==============================================================================*/

(function() {
  'use strict';

  /**
   * Utility: Debounce function
   */
  window.debounce = function(func, wait) {
    var timeout;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  };

  /**
   * Utility: Throttle function
   */
  window.throttle = function(func, limit) {
    var lastFunc;
    var lastRan;
    return function() {
      var context = this;
      var args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(function() {
          if (Date.now() - lastRan >= limit) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initSmoothScroll();
    initMobileNavEnhancements();
    initProductImageGallery();
    initCartUpdateAnimations();
    initAccessibilityEnhancements();
    initAnimateOnScroll();
    initLazyLoadNudge();
  }

  /**
   * Nudge lazysizes to re-check images
   * Useful for sliders and dynamic content
   */
  function initLazyLoadNudge() {
    if (!window.lazySizes) return;
    
    // Nudge lazysizes on thumbnail/variant clicks only (not on resize - causes loops)
    document.addEventListener('click', function(e) {
      if (e.target.closest('.product-single__thumbnail') || 
          e.target.closest('.variant-image-option') ||
          e.target.closest('.pk-thumbnail')) {
        setTimeout(function() {
          lazySizes.loader.checkElems();
        }, 300);
      }
    });
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]:not([href="#"])').forEach(function(anchor) {
      anchor.addEventListener('click', function(e) {
        var targetId = this.getAttribute('href');
        var targetElement = document.querySelector(targetId);
        
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Mobile navigation enhancements
   */
  function initMobileNavEnhancements() {
    var mobileNavTrigger = document.getElementById('MobileNavTrigger');
    var mobileNav = document.getElementById('MobileNav');
    
    if (!mobileNavTrigger || !mobileNav) return;
    
    // Close mobile nav when clicking outside
    document.addEventListener('click', function(e) {
      if (mobileNav.classList.contains('is-open')) {
        if (!mobileNav.contains(e.target) && !mobileNavTrigger.contains(e.target)) {
          closeMobileNav();
        }
      }
    });
    
    // Close mobile nav on escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        closeMobileNav();
        mobileNavTrigger.focus();
      }
    });
    
    function closeMobileNav() {
      mobileNav.classList.remove('is-open');
      mobileNavTrigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Product image gallery keyboard navigation
   */
  function initProductImageGallery() {
    var thumbnails = document.querySelectorAll('.product-single__thumbnail');
    
    thumbnails.forEach(function(thumbnail, index) {
      thumbnail.setAttribute('tabindex', '0');
      
      thumbnail.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.click();
        }
        
        // Arrow key navigation
        if (e.key === 'ArrowRight' && thumbnails[index + 1]) {
          thumbnails[index + 1].focus();
        }
        if (e.key === 'ArrowLeft' && thumbnails[index - 1]) {
          thumbnails[index - 1].focus();
        }
      });
    });
  }

  /**
   * Cart update animations
   */
  function initCartUpdateAnimations() {
    var cartCountElements = document.querySelectorAll('.cart-count');
    
    // Use MutationObserver to watch for cart count changes
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          var element = mutation.target.nodeType === 3 ? mutation.target.parentElement : mutation.target;
          animateElement(element);
        }
      });
    });
    
    cartCountElements.forEach(function(element) {
      observer.observe(element, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });
    
    function animateElement(element) {
      element.style.transform = 'scale(1.2)';
      setTimeout(function() {
        element.style.transform = 'scale(1)';
      }, 200);
    }
  }

  /**
   * Accessibility enhancements
   */
  function initAccessibilityEnhancements() {
    // Skip link functionality
    var skipLink = document.querySelector('.skip-link');
    if (skipLink) {
      skipLink.addEventListener('click', function(e) {
        e.preventDefault();
        var target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus();
        }
      });
    }
    
    // Improve focus visibility
    document.body.addEventListener('mousedown', function() {
      document.body.classList.add('using-mouse');
    });
    
    document.body.addEventListener('keydown', function(e) {
      if (e.key === 'Tab') {
        document.body.classList.remove('using-mouse');
      }
    });
  }

  /**
   * Animate elements on scroll
   */
  function initAnimateOnScroll() {
    var animatedElements = document.querySelectorAll('.animate-on-scroll');
    
    if (!animatedElements.length) return;
    
    // Use IntersectionObserver for performance
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });
      
      animatedElements.forEach(function(element) {
        observer.observe(element);
      });
    } else {
      // Fallback for older browsers
      animatedElements.forEach(function(element) {
        element.classList.add('is-visible');
      });
    }
  }

})();

